import { useState, useEffect } from 'react';
import { X, Calendar, Tag, Filter } from 'lucide-react';
import { Button } from '@/components/ui';
import { PhotoFiltersProps } from '../types/photo';
import { PROGRAM_VALUES } from '../constants';
import { LocationFilter, LocationFilterValue } from './LocationFilter';

export const PhotoFilters: React.FC<PhotoFiltersProps> = ({
  filters,
  onFiltersChange,
  onClose
}) => {
  const [localFilters, setLocalFilters] = useState(filters);

  // Update local filters when prop changes
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key: string, value: string | number) => {
    setLocalFilters(prev => ({
      ...prev,
      [key]: value === '' ? undefined : value
    }));
  };

  const handleLocationChange = (location: LocationFilterValue) => {
    setLocalFilters(prev => ({
      ...prev,
      district_name: location.district,
      subdistrict_name: location.subdistrict,
      village_id: location.village_id,
      village_name: location.village_name
    }));
  };

  const applyFilters = () => {
    onFiltersChange(localFilters);
    // Don't auto-close - let user close manually
  };

  const resetFilters = () => {
    const resetFilters = {
      page_number: 1,
      page_size: 10,
      group_size: 20,
      status: 'Under Review' as const,
      district_name: undefined,
      subdistrict_name: undefined,
      village_id: undefined,
      village_name: undefined,
      program: undefined,
      activity: undefined,
      start_date: undefined,
      end_date: undefined,
      image_id: undefined,
    };
    setLocalFilters(resetFilters);
    onFiltersChange(resetFilters);
  };

  // Use centralized program options
  const programOptions = PROGRAM_VALUES;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <div className="flex items-center">
          <Filter className="h-5 w-5 text-gray-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Filter Photos</h3>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Location Filters */}
          <div>
            <LocationFilter
              value={{
                district: localFilters.district_name,
                subdistrict: localFilters.subdistrict_name,
                village_id: localFilters.village_id,
                village_name: localFilters.village_name
              }}
              onChange={handleLocationChange}
              showVillages={true}
            />
          </div>

          {/* Program Filters */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-900 flex items-center">
              <Tag className="h-4 w-4 mr-2" />
              Program & Activity
            </h4>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Program
              </label>
              <select
                value={localFilters.program || ''}
                onChange={(e) => handleFilterChange('program', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Programs</option>
                {programOptions.map(program => (
                  <option key={program} value={program}>{program}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Activity
              </label>
              <input
                type="text"
                placeholder="e.g., TBTP Plantation"
                value={localFilters.activity || ''}
                onChange={(e) => handleFilterChange('activity', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={localFilters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Statuses</option>
                <option value="Under Review">Under Review</option>
                <option value="Selected">Selected</option>
              </select>
            </div>
          </div>

          {/* Date & Search Filters */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-900 flex items-center">
              <Calendar className="h-4 w-4 mr-2" />
              Date & Search
            </h4>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={localFilters.start_date || ''}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={localFilters.end_date || ''}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Image ID
              </label>
              <input
                type="text"
                placeholder="Search by image ID"
                value={localFilters.image_id || ''}
                onChange={(e) => handleFilterChange('image_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Pagination Settings */}
        <div className="border-t pt-6">
          <h4 className="text-sm font-medium text-gray-900 mb-4">Pagination Settings</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Villages per page
              </label>
              <select
                value={localFilters.page_size || 10}
                onChange={(e) => handleFilterChange('page_size', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Photos per village
              </label>
              <select
                value={localFilters.group_size || 20}
                onChange={(e) => handleFilterChange('group_size', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
        <Button
          variant="secondary"
          onClick={resetFilters}
        >
          Reset All
        </Button>
        
        <div className="flex space-x-3">
          <Button
            variant="secondary"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            onClick={applyFilters}
          >
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  );
};